/**
 * Provides the `openWindow` utility function for opening a URL in a new browser window, centered on the screen, with optional size constraints and a close callback.
 * @module openWindow
 */
// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
/**
 * Opens a URL in a new browser window, centered on the screen, with optional size constraints and a close callback.
 *
 * **Behavior:**
 * 1. **Environment check:** Verifies if running in a browser context with `window`, `screen`, and `document` available. Returns `null` and logs an error if not.
 * 2. **Input validation:** Takes an `options` object with `url` and `title` as required (though the code doesn't strictly enforce `title` requirement, `window.open` uses it). Optional `w` (width), `h` (height), and `onClose` callback are accepted.
 * 3. **Dimension calculation:**
 *  - Gets current screen/viewport dimensions and position, accounting for multi-monitor setups (`screenLeft`/`screenTop`).
 *  - If `w` or `h` are not provided or invalid, calculates default dimensions based on viewport size minus a 200px padding (100px each side), ensuring a minimum size.
 *  - Calculates an approximate system zoom factor based on `innerWidth` and `screen.availWidth`.
 *  - Calculates the centered `top` and `left` positions for the new window, adjusting for zoom.
 *  - Calculates the final `width` and `height` for the `window.open` features string, adjusting for zoom.
 * 4.  **Window opening:** Calls `window.open()` with the URL, title (used as window name), and a features string specifying dimensions, position, and scrollbars.
 * 5.  **Focus & close handling:**
 *  - If the window opens successfully (`window.open` returns a `Window` object):
 *  - Attempts to `focus()` the new window (might be blocked by browser).
 *  - If a valid `onClose` callback function is provided, it sets up a polling mechanism using `requestAnimationFrame` to check if `newWindow.closed` becomes `true`.
 *  - When closure is detected (or access is denied, potentially indicating closure), the `onClose` callback is executed.
 *  - If the window fails to open (`window.open` returns `null`, e.g., due to popup blocker), logs a warning.
 * 6. **Return value:** Returns the `Window` object (WindowProxy) for the newly opened window, or `null` if it failed to open.
 *
 * @since 3.3 Initial implementation.
 * @since 6.5 Converted into a utility function. This deprecated the `openCenteredWindow` function that was defined as a utility of the recipe creator.
 * @param {OpenWindowOptions} options - Configuration options for the new window.
 * @param {string} options.url - The URL to load in the new window. Required.
 * @param {string} options.title - The title for the window (also used as the `name` parameter for `window.open`, affecting reuse). Required.
 * @param {number} [options.w] - Optional. The desired width of the new window in pixels. If omitted or invalid, calculated based on viewport width minus padding.
 * @param {number} [options.h] - Optional. The desired height of the new window in pixels. If omitted or invalid, calculated based on viewport height minus padding.
 * @param {OpenWindowOnCloseCallback} [options.onClose] - Optional. A callback function to execute when the opened window is detected as closed or becomes inaccessible.
 * @return {Window | null} The `WindowProxy` object for the newly opened window, or `null` if the window could not be opened (e.g., blocked by a popup blocker).
 *
 * @example Importing the openWindow utility
 * ```typescript
 * import { openWindow } from '@automator/shared/utils/open-window';
 * ```
 *
 * @example Opening a simple centered window with default size
 * Opens '/login' in a new window named 'Login', centered, with size calculated based on viewport.
 * ```typescript
 * const loginWindow = openWindow( {
 *     url: '/login',
 *     title: __( 'Login', 'uncanny-automator' )
 * } );
 *
 * if ( loginWindow ) {
 *     console.log( __( 'Login window opened.', 'uncanny-automator' ) );
 * } else {
 *     console.log( __( 'Popup blocker might be active.', 'uncanny-automator' ) );
 * }
 * ```
 *
 * @example Opening a window with specific dimensions and a close callback
 * Opens 'https://automatorplugin.com/sign-up' in a 800x600 window named 'Sign up', and logs when it closes.
 * ```typescript
 * openWindow( {
 *     url: 'https://automatorplugin.com/sign-up',
 *     title: __( 'Sign up', 'uncanny-automator' ),
 *     w: 800,
 *     h: 600,
 *     onClose: () => {
 *         console.log( __( 'Sign up window was closed.', 'uncanny-automator' ) );
 *         // Perform actions after window close, e.g., refresh parent page data
 *     }
 * } );
 * ```
 *
 * @example Handling potential popup blocking
 * Checks the return value to see if the window was successfully opened.
 * ```typescript
 * const newWindow = openWindow( { url: '/help', title: __( 'Help', 'uncanny-automator' ) } );
 *
 * if ( ! newWindow ) {
 *     alert( __( 'Please allow popups for this site to open the help window.', 'uncanny-automator' ) );
 * }
 * ```
 *
 * @remarks
 * - Limitation: This function **must** be run in a browser environment as it relies on `window`, `screen`, `document`, and `window.open`.
 * - Limitation: The effectiveness of `window.open` and `newWindow.focus()` can be limited by browser settings and popup blockers. It's often best practice to call `openWindow` directly in response to a user action (like a click) to increase the chance of success.
 * - Limitation: The system zoom calculation is an approximation and might not be perfectly accurate across all browsers and OS configurations.
 * - Limitation: The `onClose` callback relies on polling `newWindow.closed`. This check can fail due to cross-origin security restrictions if the opened window navigates to a different origin than the parent. The function attempts to handle this by executing the callback if access is denied, but reliability can vary. More robust cross-origin communication methods (like `postMessage`) are needed for guaranteed interaction.
 * - Warning: Providing the same `title` in subsequent calls to `openWindow` might reuse an existing window with that name, depending on the browser.
 */
export function openWindow({ url, title, w, h, onClose }) {
    // Ensure code runs in a browser context
    if (!isDefined(window) || !isDefined(screen) || !isDefined(document)) {
        console.error('openWindow function requires a browser environment.');
        return null;
    }
    // Keep runtime check for onClose function type
    let validOnClose = undefined;
    if (onClose && typeof onClose === 'function') {
        validOnClose = onClose;
    }
    // Get screen position for dual screens (left/top) with fallback for cross-browser compatibility.
    // Use || 0 as fallback if properties are undefined
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
    // Determine viewport width and height with fallbacks.
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || screen.width;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || screen.height;
    // Apply padding of 100px on each side if width or height are not provided.
    const defaultWidth = Math.max(100, viewportWidth - 200); // Ensure minimum width
    const defaultHeight = Math.max(100, viewportHeight - 200); // Ensure minimum height
    const windowWidth = typeof w === 'number' && w > 0 ? w : defaultWidth;
    const windowHeight = typeof h === 'number' && h > 0 ? h : defaultHeight;
    // Calculate system zoom factor (approximation, might not be perfectly accurate).
    // Avoid division by zero if screen.availWidth is somehow 0.
    const systemZoom = screen.availWidth > 0 ? viewportWidth / screen.availWidth : 1;
    /**
     * Helper to calculate centered positions based on dimensions and zoom.
     * @param size
     * @param viewportSize
     * @param offset
     */
    const calculatePosition = (size, viewportSize, offset) => {
        // Prevent division by zero or negative zoom
        const zoomFactor = systemZoom > 0 ? systemZoom : 1;
        return ((viewportSize - size) / 2 / zoomFactor) + offset;
    };
    // Centered position for the new window.
    const left = calculatePosition(windowWidth, viewportWidth, dualScreenLeft);
    const top = calculatePosition(windowHeight, viewportHeight, dualScreenTop);
    // Calculate dimensions adjusted for zoom factor.
    const finalWidth = windowWidth / (systemZoom > 0 ? systemZoom : 1);
    const finalHeight = windowHeight / (systemZoom > 0 ? systemZoom : 1);
    // Open the new window with computed dimensions and position.
    // window.open can return null if blocked by a popup blocker.
    const newWindow = window.open(url, title, // The 'name' parameter for window.open
    `scrollbars=yes,width=${finalWidth},height=${finalHeight},top=${top},left=${left}`);
    // Focus the new window if it was successfully opened.
    if (newWindow) {
        // Browsers might prevent focusing windows not opened via direct user interaction.
        try {
            newWindow.focus();
        }
        catch (e) {
            console.warn('Could not focus the newly opened window.');
        }
        // If a valid onClose callback is provided, use requestAnimationFrame to check closure.
        if (validOnClose) {
            let isChecking = true; // Flag to control the loop
            const checkWindowClosed = () => {
                if (!isChecking) {
                    return;
                } // Stop if flag is false
                try {
                    // Check if newWindow exists and is closed.
                    if (!newWindow || newWindow.closed) {
                        isChecking = false; // Stop the loop
                        validOnClose(); // Execute the callback
                    }
                    else {
                        // If not closed, schedule the next check
                        requestAnimationFrame(checkWindowClosed);
                    }
                }
                catch (e) {
                    // Likely a cross-origin security error accessing .closed
                    console.warn('Cannot access popup window properties. Assuming closed or inaccessible.');
                    isChecking = false; // Stop the loop
                    validOnClose(); // Execute callback if window becomes inaccessible
                }
            };
            // Start the check loop
            requestAnimationFrame(checkWindowClosed);
        }
    }
    else {
        console.warn('Window opening was blocked or failed.');
    }
    return newWindow; // Return the window object or null
}
;
